import { useSyncExternalStore } from "react";

// PWA state shared outside React: the beforeinstallprompt event is captured as early as possible (imported by index.js).
let deferred = null;
let installed = false;
let waitingWorker = null;
let relatedInstalled = false;
const listeners = new Set();
const emit = () => listeners.forEach((l) => l());

// Inside the Android app (TWA) the first page has an android-app:// referrer / ?source=twa; remembered for the tab.
const TWA_KEY = "fmo_twa";
try {
  if (document.referrer.startsWith("android-app://") || new URLSearchParams(window.location.search).get("source") === "twa") sessionStorage.setItem(TWA_KEY, "1");
} catch {
  /* storage disabled */
}
const inTwa = () => {
  try {
    return sessionStorage.getItem(TWA_KEY) === "1";
  } catch {
    return false;
  }
};

export const isStandalone = () =>
  inTwa() || window.matchMedia?.("(display-mode: standalone)").matches || window.matchMedia?.("(display-mode: fullscreen)").matches || window.navigator.standalone === true;

window.addEventListener("beforeinstallprompt", (e) => {
  e.preventDefault();
  deferred = e;
  emit();
});
window.addEventListener("appinstalled", () => {
  installed = true;
  deferred = null;
  emit();
});

// Chrome hides its own "Install app" menu entry when this origin is already installed: detect it to explain.
navigator.getInstalledRelatedApps?.().then((apps) => {
  relatedInstalled = apps.length > 0;
  emit();
}).catch(() => {});

let snapshot = { canPrompt: false, installed: false, standalone: false, updateReady: false, relatedInstalled: false };
const read = () => {
  const next = { canPrompt: !!deferred, installed, standalone: isStandalone(), updateReady: !!waitingWorker, relatedInstalled };
  if (Object.keys(next).some((k) => next[k] !== snapshot[k])) snapshot = next;
  return snapshot;
};
const subscribe = (l) => {
  listeners.add(l);
  return () => listeners.delete(l);
};

export const usePwa = () => useSyncExternalStore(subscribe, read);

export async function promptInstall() {
  if (!deferred) return false;
  deferred.prompt();
  const { outcome } = await deferred.userChoice;
  deferred = null;
  if (outcome === "accepted") installed = true;
  emit();
  return true;
}

// User-requested update: one reload, only when an existing controller is replaced (never on the first install).
export function applyUpdate() {
  if (!waitingWorker || !navigator.serviceWorker.controller) return window.location.reload();
  let done = false;
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (done) return;
    done = true;
    window.location.reload();
  }, { once: true });
  waitingWorker.postMessage({ type: "SKIP_WAITING" });
}

export function registerServiceWorker() {
  if (!("serviceWorker" in navigator)) return;
  const main = document.querySelector('script[src*="/static/js/main"]')?.getAttribute("src") || "";
  const build = process.env.NODE_ENV === "production" ? (main.match(/main\.([0-9a-f]+)/)?.[1] || "prod") : "dev";
  window.addEventListener("load", async () => {
    try {
      // Remove any worker registered by older deploys outside the root scope or with another script (e.g. /api/pwa/).
      const regs = (await navigator.serviceWorker.getRegistrations?.()) || [];
      await Promise.all(regs.filter((r) => {
        const url = r.active?.scriptURL || r.waiting?.scriptURL || r.installing?.scriptURL || "";
        return r.scope !== `${window.location.origin}/` || (url && new URL(url).pathname !== "/sw.js");
      }).map((r) => r.unregister()));
      const reg = await navigator.serviceWorker.register(`/sw.js?v=${build}`, { scope: "/", updateViaCache: "none" });
      const track = (worker) => {
        if (!worker) return;
        const ready = () => {
          if (worker.state === "installed" && navigator.serviceWorker.controller && reg.waiting === worker) {
            waitingWorker = worker;
            emit();
          }
        };
        ready();
        worker.addEventListener("statechange", ready);
      };
      track(reg.waiting);
      reg.addEventListener("updatefound", () => track(reg.installing));
      document.addEventListener("visibilitychange", () => document.visibilityState === "visible" && reg.update().catch(() => {}));
    } catch {
      /* SW unsupported (private mode, iframe sandbox): the site works normally */
    }
  });
}
